import { Fragment, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Link,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  AddIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CloseIcon,
  CopyIcon,
  DeleteIcon,
  EditIcon,
  RepeatIcon,
} from '@chakra-ui/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Card from 'components/card/Card';
import SelectSearch from 'components/fields/SelectSearch';
import { useAppSelector } from 'hooks';
import { useCardPermission } from 'hooks/useCardPermission';
import { useCarriers } from 'hooks/use-carriers-hook';
import { ROLE_ADMIN } from 'variables/constants';
import { apiDeleteMvrAdminCarrierRule, apiListMvrAdminCarrierRules } from 'api';
import type { MVRAdminCarrierRule } from 'api';

import ColumnFilterHeader from './components/ColumnFilterHeader';
import MvrCarrierRuleModal from './components/MvrCarrierRuleModal';
import DeleteConfirmDialog from 'views/quote_marketplace/components/DeleteConfirmDialog';

type FilterKey =
  | 'transaction_type'
  | 'product_type'
  | 'province_code'
  | 'mvr_method'
  | 'format_confidence';

const TABLE_HEADERS: {
  label: string;
  width: number;
  filterKey?: FilterKey;
}[] = [
  { label: 'Transaction type', width: 190, filterKey: 'transaction_type' },
  { label: 'Product type', width: 140, filterKey: 'product_type' },
  { label: 'Province', width: 120, filterKey: 'province_code' },
  { label: 'Method', width: 160, filterKey: 'mvr_method' },
  { label: 'Format confidence', width: 170, filterKey: 'format_confidence' },
  { label: 'Actions', width: 140 },
];

const TABLE_MIN_WIDTH = TABLE_HEADERS.reduce((sum, h) => sum + h.width, 0);

const NONE_FILTER_VALUE = '__none__';

const EMPTY_FILTERS: Record<FilterKey, string[]> = {
  transaction_type: [],
  product_type: [],
  province_code: [],
  mvr_method: [],
  format_confidence: [],
};

const RuleDetail = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Box>
    <Text fontSize="xs" fontWeight="600" color="gray.400" mb={1}>
      {label}
    </Text>
    <Box>{children}</Box>
  </Box>
);

const labelFor = (
  labels: Map<string, string>,
  value: string | null | undefined,
) => (value ? (labels.get(value) ?? value) : '—');

const MvrCarrierRulesView = () => {
  const canView = useCardPermission([ROLE_ADMIN], 'enable-mvr-request');
  const flagsLoaded = useAppSelector(
    (state) => state.featureFlags.lastFetched !== null,
  );
  const toast = useToast();
  const qc = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const rowHoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const expandedBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const expandedBorderVar = useColorModeValue(
    'var(--chakra-colors-gray-300)',
    'var(--chakra-colors-whiteAlpha-300)',
  );

  const { carrierOptions, isLoadingCarriers } = useCarriers();
  const [carrierId, setCarrierId] = useState<number | null>(null);
  const carrierName = useMemo(
    () => carrierOptions.find((c) => c.value === carrierId)?.label ?? '',
    [carrierOptions, carrierId],
  );

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const hasFilters = Object.values(filters).some((v) => v.length > 0);

  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const toggleExpanded = (id: number) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const [formKey, setFormKey] = useState(0);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [modalSeed, setModalSeed] = useState<MVRAdminCarrierRule | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MVRAdminCarrierRule | null>(
    null,
  );

  const {
    data,
    isLoading: isLoadingRules,
    isError: isRulesError,
    isFetching: isFetchingRules,
    refetch: refetchRules,
  } = useQuery({
    queryKey: ['mvr-admin-carrier-rules', carrierId],
    queryFn: () => apiListMvrAdminCarrierRules(carrierId!),
    enabled: carrierId !== null,
  });
  const rules = useMemo(() => data?.rules ?? [], [data]);
  const options = data?.options;

  const transactionTypeLabels = useMemo(
    () => new Map(options?.transaction_types.map((o) => [o.value, o.label])),
    [options],
  );
  const productTypeLabels = useMemo(
    () => new Map(options?.product_types.map((o) => [o.value, o.label])),
    [options],
  );
  const mvrMethodLabels = useMemo(
    () => new Map(options?.mvr_methods.map((o) => [o.value, o.label])),
    [options],
  );
  const formatConfidenceLabels = useMemo(
    () => new Map(options?.format_confidences.map((o) => [o.value, o.label])),
    [options],
  );

  const filterOptions = useMemo(() => {
    const pick = (
      key: FilterKey,
      all: { value: string; label: string }[] | undefined,
    ) => {
      const values = new Set(rules.map((r) => r[key] || NONE_FILTER_VALUE));
      const present = (all ?? []).filter((o) => values.has(o.value));
      return values.has(NONE_FILTER_VALUE)
        ? [...present, { value: NONE_FILTER_VALUE, label: 'None' }]
        : present;
    };
    return {
      transaction_type: pick('transaction_type', options?.transaction_types),
      product_type: pick('product_type', options?.product_types),
      province_code: pick(
        'province_code',
        options?.provinces.map((p) => ({ value: p.code, label: p.name })),
      ),
      mvr_method: pick('mvr_method', options?.mvr_methods),
      format_confidence: pick('format_confidence', options?.format_confidences),
    };
  }, [rules, options]);

  const filteredRules = useMemo(
    () =>
      rules.filter((rule) =>
        (Object.keys(filters) as FilterKey[]).every(
          (key) =>
            filters[key].length === 0 ||
            filters[key].includes(rule[key] || NONE_FILTER_VALUE),
        ),
      ),
    [rules, filters],
  );

  const deleteMutation = useMutation({
    mutationFn: (rule: MVRAdminCarrierRule) =>
      apiDeleteMvrAdminCarrierRule(carrierId!, rule.id),
    onSuccess: () => {
      toast({ title: 'Rule removed', status: 'success' });
      qc.invalidateQueries({
        queryKey: ['mvr-admin-carrier-rules', carrierId],
      });
      qc.invalidateQueries({ queryKey: ['mvr-carriers-for-province'] });
      setDeleteTarget(null);
    },
    onError: () => toast({ title: 'Failed to remove rule', status: 'error' }),
  });

  const openModal = (
    mode: 'create' | 'edit',
    seed: MVRAdminCarrierRule | null = null,
  ) => {
    setModalMode(mode);
    setModalSeed(seed);
    setFormKey((k) => k + 1);
    onOpen();
  };

  const emptyMessage =
    isLoadingRules || isRulesError
      ? null
      : rules.length === 0
        ? "No rules yet for this carrier. It won't appear in the MVR Request form until at least one rule is added."
        : filteredRules.length === 0
          ? 'No rules match your filters.'
          : null;

  if (!flagsLoaded) return null;

  if (!canView) {
    return <Navigate to="/admin/home" replace />;
  }

  return (
    <Card flexDirection="column" w="100%" px="0px">
      <Flex px="20px" justifyContent="space-between" align="center" mb="20px">
        <Text color={textColor} fontSize="22px" fontWeight="700">
          MVR Carrier Rules
        </Text>
        <Flex gap={2}>
          <Tooltip label="Refresh rules" hasArrow>
            <IconButton
              aria-label="Refresh rules"
              icon={<RepeatIcon />}
              onClick={() => refetchRules()}
              variant="ghost"
              isLoading={isLoadingRules || isFetchingRules}
              isDisabled={carrierId === null}
            />
          </Tooltip>
          <Button
            leftIcon={<AddIcon />}
            variant="brand"
            onClick={() => openModal('create')}
            isDisabled={carrierId === null || !options}
          >
            Add rule
          </Button>
        </Flex>
      </Flex>

      <Flex px="20px" mb="20px" justify="space-between" align="flex-end">
        <FormControl maxW="460px">
          <FormLabel fontSize="sm">Carrier</FormLabel>
          <SelectSearch
            options={carrierOptions}
            value={carrierId ?? undefined}
            onChange={(v) => {
              setCarrierId(v === null ? null : Number(v));
              setFilters(EMPTY_FILTERS);
            }}
            placeholder="Select a carrier"
            isLoading={isLoadingCarriers}
          />
        </FormControl>
        {hasFilters && (
          <Button
            size="sm"
            variant="outline"
            colorScheme="brand"
            leftIcon={<CloseIcon boxSize={2.5} />}
            onClick={() => setFilters(EMPTY_FILTERS)}
          >
            Clear filters
          </Button>
        )}
      </Flex>

      {carrierId !== null && (
        <>
          <Box overflowX="auto">
            <Table
              variant="simple"
              color="gray.500"
              sx={{ tableLayout: 'fixed' }}
              minW={`${TABLE_MIN_WIDTH}px`}
            >
              <colgroup>
                {TABLE_HEADERS.map(({ label, width }) => (
                  <col key={label} style={{ width }} />
                ))}
              </colgroup>
              <Thead>
                <Tr>
                  {TABLE_HEADERS.map(({ label, filterKey }) =>
                    filterKey ? (
                      <ColumnFilterHeader
                        key={label}
                        label={label}
                        options={filterOptions[filterKey]}
                        selected={filters[filterKey]}
                        onChange={(values) =>
                          setFilters((f) => ({ ...f, [filterKey]: values }))
                        }
                      />
                    ) : (
                      <Th key={label} pe="10px" color="gray.400">
                        {label}
                      </Th>
                    ),
                  )}
                </Tr>
              </Thead>
              <Tbody>
                {isLoadingRules && (
                  <Tr>
                    <Td colSpan={TABLE_HEADERS.length} textAlign="center">
                      <Flex align="center" justify="center" mt="20px">
                        <Spinner size="sm" mr="10px" /> Loading rules...
                      </Flex>
                    </Td>
                  </Tr>
                )}
                {filteredRules.map((rule, index) => {
                  const isExpanded = expandedIds.has(rule.id);
                  const followsExpanded =
                    index > 0 && expandedIds.has(filteredRules[index - 1].id);
                  return (
                    <Fragment key={rule.id}>
                      <Tr
                        cursor="pointer"
                        bg={isExpanded ? expandedBg : undefined}
                        sx={
                          isExpanded && !followsExpanded
                            ? {
                                '& > td': {
                                  boxShadow: `inset 0 1px 0 ${expandedBorderVar}`,
                                },
                              }
                            : undefined
                        }
                        _hover={{ bg: isExpanded ? expandedBg : rowHoverBg }}
                        onClick={() => toggleExpanded(rule.id)}
                      >
                        <Td>
                          <Flex align="center" gap={2}>
                            <Icon
                              as={
                                isExpanded ? ChevronDownIcon : ChevronRightIcon
                              }
                              aria-label={isExpanded ? 'Collapse' : 'Expand'}
                            />
                            {labelFor(
                              transactionTypeLabels,
                              rule.transaction_type,
                            )}
                          </Flex>
                        </Td>
                        <Td>
                          {labelFor(productTypeLabels, rule.product_type)}
                        </Td>
                        <Td>{rule.province_code}</Td>
                        <Td>{labelFor(mvrMethodLabels, rule.mvr_method)}</Td>
                        <Td>
                          {labelFor(
                            formatConfidenceLabels,
                            rule.format_confidence,
                          )}
                        </Td>
                        <Td
                          onClick={(e) => e.stopPropagation()}
                          cursor="default"
                        >
                          <HStack spacing={1}>
                            <Tooltip label="Edit" hasArrow>
                              <IconButton
                                aria-label="Edit rule"
                                size="sm"
                                variant="ghost"
                                icon={<EditIcon />}
                                onClick={() => openModal('edit', rule)}
                              />
                            </Tooltip>
                            <Tooltip label="Duplicate" hasArrow>
                              <IconButton
                                aria-label="Duplicate rule"
                                size="sm"
                                variant="ghost"
                                icon={<CopyIcon />}
                                onClick={() => openModal('create', rule)}
                              />
                            </Tooltip>
                            <Tooltip label="Delete" hasArrow>
                              <IconButton
                                aria-label="Delete rule"
                                size="sm"
                                variant="ghost"
                                colorScheme="red"
                                icon={<DeleteIcon />}
                                onClick={() => setDeleteTarget(rule)}
                              />
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                      {isExpanded && (
                        <Tr
                          bg={expandedBg}
                          sx={{
                            '& > td': { borderBottomColor: expandedBorderVar },
                          }}
                        >
                          <Td colSpan={TABLE_HEADERS.length}>
                            <SimpleGrid
                              columns={{ base: 1, md: 2 }}
                              spacingX={8}
                              spacingY={3}
                              fontSize="sm"
                            >
                              <RuleDetail label="Portal link">
                                {rule.portal_link ? (
                                  <Link
                                    href={rule.portal_link}
                                    isExternal
                                    color="brand.500"
                                    wordBreak="break-all"
                                  >
                                    {rule.portal_link}
                                  </Link>
                                ) : (
                                  '—'
                                )}
                              </RuleDetail>
                              <RuleDetail label="Format description">
                                {rule.format_pattern_description || '—'}
                              </RuleDetail>
                              <RuleDetail label="Updated">
                                {new Date(rule.updated_at).toLocaleDateString()}
                              </RuleDetail>
                              <RuleDetail label="Updated by">
                                {rule.updated_by_name ?? '—'}
                              </RuleDetail>
                            </SimpleGrid>
                          </Td>
                        </Tr>
                      )}
                    </Fragment>
                  );
                })}
              </Tbody>
            </Table>
          </Box>

          {isRulesError && (
            <Box px="20px" my="20px">
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                Failed to load rules for this carrier. Use refresh to try again.
              </Alert>
            </Box>
          )}

          {emptyMessage && (
            <Box
              textAlign="center"
              w="100%"
              mt="20px"
              mb="20px"
              color={mutedColor}
            >
              {emptyMessage}
            </Box>
          )}
        </>
      )}

      {options && (
        <MvrCarrierRuleModal
          key={formKey}
          isOpen={isOpen}
          onClose={onClose}
          carrierId={carrierId ?? 0}
          carrierName={carrierName}
          options={options}
          seed={modalSeed}
          mode={modalMode}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => deleteMutation.mutate(deleteTarget)}
          entityName="Carrier Rule"
          itemTitle={`${labelFor(transactionTypeLabels, deleteTarget.transaction_type)} / ${deleteTarget.province_code}`}
        />
      )}
    </Card>
  );
};

export default MvrCarrierRulesView;
